/**
 * useViewPrefs (iter1073) — потребителят сам маха секции от един екран.
 * Изборът се пази за неговия профил (`/api/view-prefs/{scope}`), а докато
 * заявката пътува, се ползва и местно копие, за да няма трептене.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useViewPrefs(scope, defaultHidden = []) {
  const key = `view_prefs_${scope}`;
  const [hidden, setHidden] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return defaultHidden;
  });
  const [ready, setReady] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const firstLoad = useRef(true);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/view-prefs/${scope}`)
      .then(({ data }) => {
        if (!live) return;
        const server = data?.hidden;
        if (Array.isArray(server) && server.length) setHidden(server);
        setReady(true);
      })
      .catch(() => setReady(true));
    return () => { live = false; };
  }, [scope]);

  const persist = useCallback((next) => {
    setHidden(next);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* noop */ }
    axios.put(`${API}/view-prefs/${scope}`, { hidden: next }).catch(() => {});
  }, [key, scope]);

  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return; }
  }, [hidden]);

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);
  const toggle = useCallback((id) => {
    persist(hiddenSet.has(id) ? hidden.filter((x) => x !== id) : [...hidden, id]);
  }, [hidden, hiddenSet, persist]);
  const reset = useCallback(() => persist([]), [persist]);

  return { hidden, hiddenSet, isHidden: (id) => hiddenSet.has(id),
           toggle, reset, editMode, setEditMode, ready };
}

export default useViewPrefs;
